<template>
	<BaseModal :show="show">
		<form class="flex flex-col h-fit w-[420px] bg-neutral">
			<div class="flex w-full align-middle">
				<p class="w-full text-xl font-bold text-heading">{{ props.title }}</p>
				<svg class="ml-auto cursor-pointer my-auto" @click="onDismiss" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 18 18" fill="none">
					<title>Close</title>
					<path d="M9.9002 8.99922L16.7627 2.13672C17.0158 1.88359 17.0158 1.48984 16.7627 1.23672C16.5096 0.983594 16.1158 0.983594 15.8627 1.23672L9.0002 8.09922L2.1377 1.23672C1.88457 0.983594 1.49082 0.983594 1.2377 1.23672C0.98457 1.48984 0.98457 1.88359 1.2377 2.13672L8.10019 8.99922L1.2377 15.8617C0.98457 16.1148 0.98457 16.5086 1.2377 16.7617C1.3502 16.8742 1.51895 16.9586 1.6877 16.9586C1.85645 16.9586 2.0252 16.9023 2.1377 16.7617L9.0002 9.89922L15.8627 16.7617C15.9752 16.8742 16.1439 16.9586 16.3127 16.9586C16.4814 16.9586 16.6502 16.9023 16.7627 16.7617C17.0158 16.5086 17.0158 16.1148 16.7627 15.8617L9.9002 8.99922Z"
							class="fill-icon-neutral"/>
				</svg>
			</div>
			<p class="font-medium leading-6 text-grayscale-body mt-1 mb-4" v-html="props.message"/>

			<InputText
				v-model="enteredText"
				type="textarea"
				:max-length="maxLength"
				text-area-rows="3"
				:placeholder="placeholder"
				input-height="h-10"
				font-weight="font-normal"
				:disabled="disabled"
				:input-filter="inputFilter"
			/>

			<div class="flex flex-row justify-between items-center">
				<span v-if="subInputMessage && subInputMessage.length > 0" class="text-grayscale-body">{{ subInputMessage }}</span><span v-if="props.maxLength" class="text-xs ml-auto text-nowrap">{{ characterCountText }}</span>
			</div>

			<div v-if="buttons.length > 0" class="flex flex-row mt-5 ml-auto space-x-4">
				<SubmitButton v-for="buttonData in buttons"
				              :key="buttonData.label"
				              :type="buttonData.type"
				              :size="buttonData.size"
				              :label="buttonData.label"
				              :disabled="buttonData.isLoading"
				              :loading="buttonData.isLoading"
				              @click.prevent="buttonData.onClick"
				/>
			</div>

			<div v-if="twoButtonModal" class="flex flex-row mt-5 ml-auto space-x-4">
				<SubmitButton label="Cancel" :type="ButtonType.Outline" :size="ButtonSize.Small" @click.prevent="onDismiss()" />
				<SubmitButton :label="buttonText" @click.prevent="onButtonClicked" :size="buttonSize" :type="buttonType" :width="buttonWidth" :loading="loading" :disabled="disableSaveButton" />
			</div>

			<div v-else class="flex flex-row mt-4 space-x-5" :class="buttonWidth === ButtonWidth.Full ? 'w-full' : 'mx-auto'">
				<SubmitButton class="w-full" :size="buttonSize" :label="buttonText" :type="buttonType" :width="buttonWidth" :loading="loading" :disabled="disableSaveButton" @click.prevent="onButtonClicked" />
			</div>

		</form>
	</BaseModal>
</template>

<script setup lang="js">
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { ButtonSize, ButtonType, ButtonWidth } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import InputText from '@/components/Forms/InputText.vue'
import { computed, ref, watch } from 'vue'
import BaseModal from '@/components/Alerts/BaseModal.vue'

const props = defineProps({
	show: { type: Boolean, default: false },
	title: { type: String, default: '' },
	message: { type: String, default: '' },
	subInputMessage: { type: String, default: '' },
	placeholder: { type: String, default: '' },
	defaultText: { type: String, default: '' },
	buttons: { type: Array, default: () => [] },//Set for multiple buttons
	twoButtonModal: { type: Boolean, default: false },//Set for multiple buttons
	maxLength: { type: Number, default: null },
	buttonText: { type: String, default: '' },
	buttonType: {
		type: String,
		default: ButtonType.Default,
		validator(value)
		{
			return ButtonType.isValid(value)
		}
	},
	buttonSize: {
		type: String,
		default: ButtonSize.Default,
		validator(value)
		{
			return ButtonSize.isValid(value)
		}
	},
	buttonWidth: {
		type: String,
		default: ButtonWidth.Fit,
		validator(value)
		{
			return ButtonWidth.isValid(value)
		}
	},
	loading: { type: Boolean, default: false },
	allowEmpty: { type: Boolean, default: true },
	disabled: { type: Boolean, default: false },
	inputFilter: { type: Function, default: null },
})

const enteredText = ref(props.defaultText)
const emit = defineEmits(['dismiss', 'onButtonClicked'])

const characterCountText = computed(() => {
	const enteredCount = enteredText.value?.length ?? 0
	const maxCount = props.maxLength ?? 100

	return `${enteredCount} / ${maxCount}`
})

const disableSaveButton = computed(() => {
	const enteredCount = enteredText.value?.length ?? 0
	return props.loading || (!props.allowEmpty && enteredCount === 0)
})

watch(
	() => props.defaultText,
	(newValue) =>
	{
		if (newValue)
		{
			enteredText.value = newValue
		}
	}
)

function onButtonClicked()
{
	if (!props.disabled) {
		emit('onButtonClicked', enteredText.value)
	}

	onDismiss()
}

function reset() {
	enteredText.value = ''
}

function onDismiss() {
	reset()
	emit('dismiss')
}

</script>